/**
 * Client Error Reporter Component
 * Lightweight error logging for production visibility
 * Logs commit hash, route, and error details to server
 */

'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

export function ClientErrorReporter() {
  const pathname = usePathname();

  useEffect(() => {
    // Get commit hash from environment (set by Vercel)
    const commitSha = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || 'local';

    // Log error to server endpoint
    const logError = async (error: Error, errorInfo?: string) => {
      try {
        await fetch('/api/client-error', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            commitSha,
            pathname: pathname || window.location.pathname,
            message: error.message || 'Unknown error',
            stack: error.stack || errorInfo || 'No stack trace',
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
            timestamp: new Date().toISOString(),
          }),
        }).catch(() => {
          // Silently fail - don't break the app if logging fails
        });
      } catch (e) {
        // Silently fail - don't break the app if logging fails
      }
    };

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error 
        ? event.reason 
        : new Error(String(event.reason || 'Unhandled promise rejection'));
      
      console.error('[ClientErrorReporter] Unhandled rejection:', error);
      logError(error, 'Unhandled promise rejection');
    };

    // Handle global JavaScript errors
    const handleError = (event: ErrorEvent) => {
      // Ignore errors from browser extensions
      if (
        event.filename &&
        (event.filename.includes('chrome-extension://') ||
         event.filename.includes('moz-extension://') ||
         event.filename.includes('safari-extension://'))
      ) {
        return;
      }

      const error = event.error instanceof Error
        ? event.error
        : new Error(event.message || 'Unknown error');

      console.error('[ClientErrorReporter] Global error:', error);
      logError(error, event.filename || 'Unknown source');
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, [pathname]);

  // This component doesn't render anything
  return null;
}
